import { zodResolver } from "@hookform/resolvers/zod";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import type { Resolver } from "react-hook-form";
import { useForm } from "react-hook-form";

import { defaultHealthAdditionalInfoValues } from "@/D2C/features/health-quote/redux/health-quote/healthQuoteSlice";
import {
  ConfirmationCheckbox,
  CountrySelect,
  RadioInput,
  SelectInput,
  TextInput,
} from "@/shared/components";
import { getHomeCountryLabel } from "@/shared/config/localisation";
import type { StepFormComponentProps, StepFormHandle } from "@/shared/stepper/types";
import type { StructuredDocumentData } from "@/shared/types/documentuploadtype";
import {
  formatNationalId,
  getNationalIdLabel,
  getNationalIdMaxLength,
  getNationalIdOcrKeys,
  getNationalIdPlaceholder,
} from "@/shared/utils/nationalId";
import { healthAdditionalInfoSchema } from "@/shared/validation/healthQuoteSchemas";
import type { HealthAdditionalInfoValues } from "../../redux/type";

const GENDER_OPTIONS = [
  { label: "Male", value: "Male" },
  { label: "Female", value: "Female" },
];

const SOURCE_OF_FUND_OPTIONS = [
  { label: "Employment / Salary", value: "Employment" },
  { label: "Business", value: "Business" },
  { label: "Investment / Savings", value: "Investment" },
  { label: "Other", value: "Other" },
];

const BENEFICIARY_RELATION_OPTIONS = [
  { label: "Legal Heir", value: "Legal Heir" },
  { label: "Other", value: "Other" },
];

type HealthAdditionalInfoStepExtraProps = {
  isReadOnly?: boolean;
  ocrStructuredData?: StructuredDocumentData;
  onResidentConfirmChange?: (checked: boolean) => boolean | Promise<boolean> | void;
};

const HealthAdditionalInfoStep = forwardRef<
  StepFormHandle<HealthAdditionalInfoValues>,
  StepFormComponentProps<HealthAdditionalInfoValues, HealthAdditionalInfoStepExtraProps>
>(function HealthAdditionalInfoStep(
  { defaultValues, isReadOnly = false, ocrStructuredData, onResidentConfirmChange },
  ref
) {
  const resolvedDefaults = defaultValues ?? defaultHealthAdditionalInfoValues;
  const nationalIdLabel = getNationalIdLabel();
  const nationalIdPlaceholder = getNationalIdPlaceholder();
  const nationalIdMaxLength = getNationalIdMaxLength();

  const form = useForm<HealthAdditionalInfoValues>({
    resolver: zodResolver(healthAdditionalInfoSchema) as Resolver<HealthAdditionalInfoValues>,
    defaultValues: resolvedDefaults,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  // Hydrate form when navigating back
  useEffect(() => {
    form.reset(resolvedDefaults);
  }, [form, resolvedDefaults]);

  // Autofill from OCR structured data when available
  useEffect(() => {
    if (!ocrStructuredData) return;

    const { first_name, last_name, gender, nationality } = ocrStructuredData as any;
    const rawNationalId =
      (ocrStructuredData as any)?.emirates_id ||
      getNationalIdOcrKeys()
        .map((key) => (ocrStructuredData as any)?.[key])
        .find((value) => typeof value === "string" && value.trim());

    if (gender && ["Male", "Female"].includes(gender)) {
      form.setValue("gender", gender as "Male" | "Female", { shouldDirty: true });
    }
    if (nationality) {
      form.setValue("nationality", nationality, { shouldDirty: true });
    }
    if (rawNationalId) {
      form.setValue("emiratesId", formatNationalId(rawNationalId), {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    void first_name;
    void last_name;
  }, [ocrStructuredData, form]);

  useImperativeHandle(ref, () => ({
    submit: async () => {
      if (isReadOnly) {
        return true;
      }

      return form.trigger(undefined, { shouldFocus: true });
    },
    getValues: () => form.getValues(),
  }));

  const residentConfirmationText = `I confirm I am a resident of ${getHomeCountryLabel()}.`;

  return (
    <div className="space-y-5">
      <fieldset disabled={isReadOnly} className="contents">
        {/* Row 1 — gender + nationality */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <RadioInput
            control={form.control}
            name="gender"
            label="Gender"
            options={GENDER_OPTIONS}
            required
          />
          <CountrySelect
            control={form.control}
            name="nationality"
            label="Nationality"
            placeholder="Select nationality"
            required
          />
        </div>

        {/* Row 2 — city + address */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextInput
            control={form.control}
            name="cityOfResidence"
            label="City of Residence"
            placeholder="Enter City"
            required
          />
          <TextInput
            control={form.control}
            name="address"
            label="Address"
            placeholder="Enter full address"
            required
          />
        </div>

        {/* Row 3 — ID document + passport */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextInput
            control={form.control}
            name="emiratesId"
            label={nationalIdLabel}
            placeholder={nationalIdPlaceholder}
            required
            formatValueOnChange={formatNationalId}
            formatValueOnBlur={formatNationalId}
            inputMode="numeric"
            maxLength={nationalIdMaxLength}
          />
          <TextInput
            control={form.control}
            name="passportNumber"
            label="Passport Number (optional)"
            placeholder="e.g. A1234567"
          />
        </div>

        {/* Row 4 — source of fund + beneficiary name */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SelectInput
            control={form.control}
            name="sourceOfFund"
            label="Source of Fund"
            placeholder="Select Source"
            options={SOURCE_OF_FUND_OPTIONS}
            required
          />
          <TextInput
            control={form.control}
            name="beneficiaryName"
            label="Beneficiary Name"
            placeholder="Enter Full Name"
            required
          />
        </div>

        {/* Row 5 — beneficiary relation */}
        <div className="sm:w-1/2">
          <SelectInput
            control={form.control}
            name="beneficiaryRelation"
            label="Beneficiary Relation"
            placeholder="Select Relation"
            options={BENEFICIARY_RELATION_OPTIONS}
            required
          />
        </div>

        {/* UAE resident confirmation */}
        <ConfirmationCheckbox
          checked={form.watch("uaeResidentConfirm") === true}
          disabled={isReadOnly}
          error={form.formState.errors.uaeResidentConfirm?.message ?? ""}
          onChange={async (checked) => {
            if (checked) {
              form.setValue("uaeResidentConfirm", true, {
                shouldValidate: true,
                shouldDirty: true,
              });
              const isValid = await form.trigger(undefined, { shouldFocus: true });
              if (!isValid) {
                form.setValue("uaeResidentConfirm", false, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
                return;
              }
            } else {
              form.setValue("uaeResidentConfirm", false, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }

            queueMicrotask(async () => {
              const accepted = await onResidentConfirmChange?.(checked);
              if (checked && accepted === false) {
                form.setValue("uaeResidentConfirm", false, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }
            });
          }}
        >
          {residentConfirmationText}
        </ConfirmationCheckbox>
      </fieldset>
    </div>
  );
});

export default HealthAdditionalInfoStep;
